import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { Navigation } from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Video, Sparkles, Loader2, Play, Check, Trash2, Copy,
  ArrowLeft, Film, Wand2, Eye, EyeOff, Plus, X, Layers, Mic, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const PRESET_PROMPTS = [
  {
    id: 'welcome',
    label: 'Welcome / Promo (Bundle style)',
    prompt: `Cinematic modern promo for an exclusive golf club app called "The Bundle Golf Club". High-energy montage, premium mobile-app aesthetic. Quick cuts: a confident foursome on a pristine tee box pulling out phones, close-ups of a clean leaderboard UI glowing on a smartphone, golfers laughing and fist-bumping after a birdie, dramatic drive swing with club-head motion blur, an animated leaderboard scrolling with player names, stacks of poker chips and a winning putt dropping, groups forming at the clubhouse at sunset, and a final triumphant walk-off silhouetted against a golden sunset sky. Gold and deep-green color grading, smooth camera moves, Masters-inspired luxury feel, text-free composition.`,
  },
  {
    id: 'tournament',
    label: 'Tournament hype',
    prompt: `Cinematic tournament hype reel: 4 golfers teeing off in unison, slow-motion iron shot carving a divot, ball dropping in a cup on a crisp green, leaderboard chyron pulsing with rising rankings, crowd cheering behind ropes, a gold-plated trophy glinting in late-afternoon sun. Masters green and gold palette, high-end sports broadcast feel.`,
  },
  {
    id: 'skins',
    label: 'Skins game energy',
    prompt: `Stylish skins-game promo: cash piling up after each hole, golfers checking phones with glowing green+gold UI notifications, dramatic putts dropping, chips stacking, and a final fist-pump celebration as the leaderboard shifts. Smooth transitions, bokeh highlights, premium color grading.`,
  },
];

export default function AdminVideoMaker() {
  const { user, loading, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [mode, setMode] = useState('single'); // 'single' | 'multi'
  const [videos, setVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(true);
  const [prompt, setPrompt] = useState(PRESET_PROMPTS[0].prompt);
  const [title, setTitle] = useState('Welcome to The Bundle');
  const [duration, setDuration] = useState(8);
  const [size, setSize] = useState('1280x720');
  const [model] = useState('sora-2');
  const [submitting, setSubmitting] = useState(false);

  // Multi-scene state
  const [msTitle, setMsTitle] = useState('Welcome to The Bundle — 45s Promo');
  const [msVoice, setMsVoice] = useState('onyx');
  const [msSize, setMsSize] = useState('1280x720');
  const [msModel] = useState('sora-2');
  const [scenes, setScenes] = useState([]);
  const [loadingPreset, setLoadingPreset] = useState(false);

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) {
      navigate('/');
    }
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user?.is_admin) fetchVideos();
  }, [user]);

  // Poll every 8s while any video is generating
  useEffect(() => {
    const hasGenerating = videos.some(v => v.status === 'generating');
    if (!hasGenerating) return;
    const iv = setInterval(() => fetchVideos(false), 8000);
    return () => clearInterval(iv);
  }, [videos]);

  const fetchVideos = async (showLoader = true) => {
    if (showLoader) setLoadingVideos(true);
    try {
      const res = await fetch(`${API}/videos/list`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setVideos(data.videos || []);
      }
    } catch {}
    if (showLoader) setLoadingVideos(false);
  };

  const handleGenerate = async () => {
    if (!prompt || prompt.length < 10) {
      toast.error('Prompt is too short');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/videos/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ prompt, title, duration, size, model }),
      });
      if (res.ok) {
        toast.success('Generation started — this will take 2–5 min');
        fetchVideos();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to start generation');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const activate = async (videoId) => {
    try {
      const res = await fetch(`${API}/videos/${videoId}/activate`, { method: 'POST', credentials: 'include' });
      if (res.ok) { toast.success('Set as active welcome video'); fetchVideos(); }
      else toast.error('Failed');
    } catch { toast.error('Network error'); }
  };

  const deactivate = async (videoId) => {
    try {
      const res = await fetch(`${API}/videos/${videoId}/deactivate`, { method: 'POST', credentials: 'include' });
      if (res.ok) { toast.success('Removed from homepage'); fetchVideos(); }
    } catch {}
  };

  const del = async (videoId) => {
    if (!window.confirm('Delete this video permanently?')) return;
    try {
      const res = await fetch(`${API}/videos/${videoId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Deleted'); fetchVideos(); }
    } catch {}
  };

  const copyPrompt = (p) => {
    navigator.clipboard.writeText(p);
    toast.success('Prompt copied');
  };

  // ───── Multi-scene helpers ─────
  const loadBundlePreset = async (variant = 'bundle-promo') => {
    setLoadingPreset(true);
    try {
      const res = await fetch(`${API}/videos/multi-scene/preset/${variant}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMsTitle(data.title);
        setMsVoice(data.voice);
        setScenes(data.scenes);
        toast.success(`${variant === 'bundle-promo-global' ? 'Global Network' : 'Bundle promo'} template loaded`);
      }
    } catch {}
    setLoadingPreset(false);
  };

  const addScene = () => {
    if (scenes.length >= 10) { toast.error('Max 10 scenes'); return; }
    setScenes([...scenes, { duration: 4, visual_prompt: '', voiceover: '' }]);
  };

  const updateScene = (i, key, value) => {
    setScenes(scenes.map((s, idx) => idx === i ? { ...s, [key]: value } : s));
  };

  const removeScene = (i) => {
    setScenes(scenes.filter((_, idx) => idx !== i));
  };

  const totalSceneDuration = scenes.reduce((sum, s) => sum + (s.duration || 0), 0);

  const handleGenerateMultiScene = async () => {
    if (scenes.length === 0) { toast.error('Add at least one scene'); return; }
    for (const s of scenes) {
      if (!s.visual_prompt || s.visual_prompt.length < 10) {
        toast.error('Every scene needs a visual prompt (min 10 chars)');
        return;
      }
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/videos/multi-scene/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: msTitle, size: msSize, model: msModel, voice: msVoice,
          scenes: scenes.map(s => ({
            visual_prompt: s.visual_prompt,
            duration: s.duration,
            voiceover: s.voiceover || null,
          })),
        }),
      });
      if (res.ok) {
        toast.success(`Multi-scene generation started · ${scenes.length} scenes · ~${scenes.length * 3}-${scenes.length * 5} min`);
        fetchVideos();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !user?.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bundle-cream">
        <Loader2 className="w-8 h-8 animate-spin text-bundle-green" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bundle-cream pb-mobile-nav" data-testid="admin-video-maker">
      <Navigation user={user} onLogout={logout} />

      <div className="max-w-5xl mx-auto px-4 md:px-8 pt-20 md:pt-24">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin')}
          className="mb-4 text-bundle-green hover:bg-bundle-cream-dark -ml-2"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin
        </Button>

        <div className="mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-bundle-gold/15 border border-bundle-gold/30 mb-3">
            <Sparkles className="w-3.5 h-3.5 text-bundle-gold-dark" />
            <span className="font-body text-[10px] uppercase tracking-widest font-bold text-bundle-gold-dark">Sora 2 · AI Video</span>
          </div>
          <h1 className="font-heading font-bold text-3xl text-bundle-green mb-2 flex items-center gap-2">
            <Film className="w-7 h-7" /> Video Maker
          </h1>
          <p className="font-body text-gray-600">
            Generate cinematic promo videos from a text prompt. Set one as the active welcome video to display at the top of the Member Dashboard.
          </p>
        </div>

        {/* Mode tabs */}
        <div className="mb-6 inline-flex bg-white border border-bundle-cream-dark rounded-full p-1 shadow-sm">
          <button
            onClick={() => setMode('single')}
            data-testid="mode-single"
            className={`flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-bold transition-all ${
              mode === 'single' ? 'bg-bundle-green text-white shadow-md' : 'text-bundle-green hover:bg-bundle-cream'
            }`}
          >
            <Zap className="w-4 h-4" /> Single Clip
          </button>
          <button
            onClick={() => setMode('multi')}
            data-testid="mode-multi"
            className={`flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-bold transition-all ${
              mode === 'multi' ? 'bg-bundle-green text-white shadow-md' : 'text-bundle-green hover:bg-bundle-cream'
            }`}
          >
            <Layers className="w-4 h-4" /> Multi-Scene Builder
          </button>
        </div>

        {/* Generator */}
        {mode === 'single' ? (
        <Card className="mb-8 bg-white border-bundle-cream-dark">
          <CardContent className="p-6 space-y-4">
            {/* Preset prompts */}
            <div>
              <Label className="text-sm text-gray-600 mb-2 block">Quick Start Prompts</Label>
              <div className="flex flex-wrap gap-2">
                {PRESET_PROMPTS.map(p => (
                  <button
                    key={p.id}
                    onClick={() => { setPrompt(p.prompt); setTitle(p.label); }}
                    data-testid={`preset-${p.id}`}
                    className="px-3 py-1.5 rounded-full border border-bundle-cream-dark bg-white text-xs font-bold text-bundle-green hover:border-bundle-gold hover:bg-bundle-gold/5 transition-colors"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm text-gray-600">Title</Label>
              <Input
                data-testid="video-title-input"
                value={title}
                onChange={e => setTitle(e.target.value)}
                className="mt-1"
                placeholder="e.g. Welcome to The Bundle"
              />
            </div>

            <div>
              <Label className="text-sm text-gray-600">Prompt *</Label>
              <textarea
                data-testid="video-prompt-input"
                rows={7}
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-bundle-green"
                placeholder="Describe the video you want..."
              />
              <p className="text-[11px] text-gray-400 mt-1">
                {prompt.length} chars. Max 4000.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm text-gray-600">Duration</Label>
                <select
                  data-testid="video-duration-select"
                  value={duration}
                  onChange={e => setDuration(parseInt(e.target.value))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bundle-green"
                >
                  <option value={4}>4 sec (fastest)</option>
                  <option value={8}>8 sec (recommended)</option>
                  <option value={12}>12 sec (max, slow)</option>
                </select>
              </div>
              <div>
                <Label className="text-sm text-gray-600">Format</Label>
                <select
                  data-testid="video-size-select"
                  value={size}
                  onChange={e => setSize(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bundle-green"
                >
                  <option value="1280x720">Landscape HD 16:9</option>
                  <option value="1792x1024">Wide 16:9 (HQ)</option>
                  <option value="1024x1792">Vertical 9:16 (mobile)</option>
                  <option value="1024x1024">Square 1:1</option>
                </select>
              </div>
            </div>

            {/* Cost estimate */}
            <div
              className="flex items-start gap-2 rounded-md border border-bundle-gold/30 bg-bundle-gold/5 px-3 py-2 text-[12px] text-bundle-green-dark"
              data-testid="cost-estimate-single"
            >
              <Sparkles className="w-3.5 h-3.5 text-bundle-gold-dark mt-0.5 flex-shrink-0" />
              <span>
                <strong>Estimated cost:</strong> ~${(duration * 0.10).toFixed(2)} per generation
                {size !== '1280x720' && <span className="text-gray-500"> (higher for {size})</span>}
                <span className="text-gray-500"> · Sora 2 · {duration}s</span>
              </span>
            </div>

            <Button
              onClick={handleGenerate}
              disabled={submitting || !prompt || prompt.length < 10}
              data-testid="video-generate-btn"
              className="w-full bg-bundle-gold text-bundle-green-dark hover:bg-bundle-gold-dark font-bold py-6"
            >
              {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Wand2 className="w-5 h-5 mr-2" />}
              Generate Video
            </Button>

            <p className="text-xs text-gray-500 text-center">
              Sora 2 takes 2–5 minutes. You can close this page — generation continues in the background.
            </p>
          </CardContent>
        </Card>
        ) : (
        <Card className="mb-8 bg-white border-bundle-cream-dark">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="px-3 py-1 rounded-full bg-bundle-gold/15 border border-bundle-gold/30 inline-flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5 text-bundle-gold-dark" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-bundle-gold-dark">Multi-Scene Builder</span>
              </div>
              <div className="ml-auto flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadBundlePreset('bundle-promo')}
                  disabled={loadingPreset}
                  data-testid="load-preset-btn"
                  className="text-xs"
                >
                  {loadingPreset ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                  Load Original Promo
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadBundlePreset('bundle-promo-global')}
                  disabled={loadingPreset}
                  data-testid="load-preset-global-btn"
                  className="text-xs border-bundle-green text-bundle-green hover:bg-bundle-green hover:text-white"
                >
                  {loadingPreset ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                  Load Global Network Promo
                </Button>
              </div>
            </div>

            <p className="text-sm text-gray-600">
              Generate multiple Sora 2 clips, optionally narrate each scene with AI voiceover, and stitch everything into one final video.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label className="text-sm text-gray-600">Final Video Title</Label>
                <Input value={msTitle} onChange={e => setMsTitle(e.target.value)} className="mt-1" data-testid="ms-title-input" />
              </div>
              <div>
                <Label className="text-sm text-gray-600">Format</Label>
                <select
                  value={msSize} onChange={e => setMsSize(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bundle-green"
                >
                  <option value="1280x720">Landscape HD 16:9</option>
                  <option value="1024x1792">Vertical 9:16</option>
                  <option value="1024x1024">Square 1:1</option>
                </select>
              </div>
              <div>
                <Label className="text-sm text-gray-600">Voice</Label>
                <select
                  value={msVoice} onChange={e => setMsVoice(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bundle-green"
                  data-testid="ms-voice-select"
                >
                  <option value="onyx">Onyx — deep, authoritative</option>
                  <option value="ash">Ash — clear, articulate</option>
                  <option value="echo">Echo — smooth, calm</option>
                  <option value="nova">Nova — energetic, upbeat</option>
                  <option value="sage">Sage — wise, measured</option>
                  <option value="shimmer">Shimmer — bright, cheerful</option>
                  <option value="alloy">Alloy — neutral</option>
                  <option value="coral">Coral — warm, friendly</option>
                  <option value="fable">Fable — expressive</option>
                </select>
              </div>
            </div>

            {/* Scenes */}
            <div className="space-y-3" data-testid="scenes-list">
              {scenes.map((scene, i) => (
                <div key={i} className="rounded-xl border-2 border-bundle-cream-dark bg-bundle-cream/30 p-4" data-testid={`scene-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 rounded-full bg-bundle-green text-white text-xs font-black flex items-center justify-center">{i+1}</span>
                      <span className="font-heading font-bold text-bundle-green">Scene {i+1}</span>
                      <select
                        value={scene.duration}
                        onChange={e => updateScene(i, 'duration', parseInt(e.target.value))}
                        className="text-xs rounded border border-gray-300 px-2 py-1"
                      >
                        <option value={4}>4s</option>
                        <option value={8}>8s</option>
                        <option value={12}>12s</option>
                      </select>
                    </div>
                    <button onClick={() => removeScene(i)} className="text-red-500 hover:bg-red-50 p-1 rounded" data-testid={`remove-scene-${i}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-[11px] text-gray-500 flex items-center gap-1"><Film className="w-3 h-3" />Visual Prompt</Label>
                      <textarea
                        rows={3}
                        value={scene.visual_prompt}
                        onChange={e => updateScene(i, 'visual_prompt', e.target.value)}
                        className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-bundle-green"
                        placeholder="What should happen on screen?"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] text-gray-500 flex items-center gap-1"><Mic className="w-3 h-3" />Voiceover (optional)</Label>
                      <Input
                        value={scene.voiceover || ''}
                        onChange={e => updateScene(i, 'voiceover', e.target.value)}
                        className="mt-1 text-xs"
                        placeholder="Narration line for this scene"
                      />
                    </div>
                  </div>
                </div>
              ))}

              <Button
                variant="outline"
                onClick={addScene}
                className="w-full border-dashed border-2 py-6 text-bundle-green hover:bg-bundle-green/5"
                data-testid="add-scene-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Scene
              </Button>
            </div>

            {scenes.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-bundle-green/5 rounded-lg border border-bundle-green/20">
                  <span className="text-sm font-bold text-bundle-green">{scenes.length} scenes · {totalSceneDuration}s total</span>
                  <span className="text-xs text-gray-500">~{Math.ceil(scenes.length * 1.5)}–{scenes.length * 3} min to generate</span>
                </div>
                <div
                  className="flex items-start gap-2 rounded-md border border-bundle-gold/30 bg-bundle-gold/5 px-3 py-2 text-[12px] text-bundle-green-dark"
                  data-testid="cost-estimate-multi"
                >
                  <Sparkles className="w-3.5 h-3.5 text-bundle-gold-dark mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Estimated cost:</strong> ~${(totalSceneDuration * 0.10).toFixed(2)} for video
                    {scenes.some(s => s.voiceover && s.voiceover.trim()) && <span> + ~$0.05 voiceover</span>}
                    <span className="text-gray-500"> · Sora 2 · {scenes.length} clips · {totalSceneDuration}s</span>
                  </span>
                </div>
              </div>
            )}

            <Button
              onClick={handleGenerateMultiScene}
              disabled={submitting || scenes.length === 0}
              data-testid="ms-generate-btn"
              className="w-full bg-bundle-gold text-bundle-green-dark hover:bg-bundle-gold-dark font-bold py-6"
            >
              {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Wand2 className="w-5 h-5 mr-2" />}
              Generate Multi-Scene Video
            </Button>

            <p className="text-xs text-gray-500 text-center">
              Each Sora 2 scene takes 1–3 min. Keep this page open or come back later — generation continues in the background.
            </p>
          </CardContent>
        </Card>
        )}

        {/* Video list */}
        <h2 className="font-heading font-bold text-2xl text-bundle-green mb-4 flex items-center gap-2">
          <Video className="w-6 h-6" /> Generated Videos
          <span className="text-sm font-normal text-gray-500">({videos.length})</span>
        </h2>

        {loadingVideos ? (
          <div className="py-12 text-center">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-bundle-green" />
          </div>
        ) : videos.length === 0 ? (
          <Card className="border-dashed border-2 border-bundle-cream-dark bg-white">
            <CardContent className="p-12 text-center">
              <Film className="w-16 h-16 mx-auto text-gray-300 mb-3" />
              <p className="text-sm text-gray-500">No videos yet. Generate your first one above.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {videos.map(v => (
              <VideoCard
                key={v.video_id}
                v={v}
                onActivate={() => activate(v.video_id)}
                onDeactivate={() => deactivate(v.video_id)}
                onDelete={() => del(v.video_id)}
                onCopyPrompt={() => copyPrompt(v.prompt)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function VideoCard({ v, onActivate, onDeactivate, onDelete, onCopyPrompt }) {
  const statusColor = v.status === 'ready' ? 'bg-green-100 text-green-700 border-green-200'
    : v.status === 'generating' ? 'bg-amber-100 text-amber-700 border-amber-200'
    : 'bg-red-100 text-red-700 border-red-200';

  return (
    <Card className={`bg-white overflow-hidden ${v.active ? 'border-bundle-gold border-2 shadow-lg shadow-bundle-gold/20' : 'border-bundle-cream-dark'}`} data-testid={`video-card-${v.video_id}`}>
      <CardContent className="p-0">
        {v.active && (
          <div className="bg-bundle-gold text-bundle-green-dark px-3 py-1.5 text-xs font-bold flex items-center justify-center gap-1.5">
            <Check className="w-3.5 h-3.5" /> LIVE ON MEMBER DASHBOARD
          </div>
        )}
        <div className="aspect-video bg-black flex items-center justify-center relative">
          {v.status === 'ready' && v.url ? (
            <video
              src={`${window.location.origin}${v.url}`}
              controls
              className="w-full h-full object-contain"
              playsInline
            />
          ) : v.status === 'generating' ? (
            <div className="text-center p-6">
              <Loader2 className="w-10 h-10 mx-auto animate-spin text-bundle-gold mb-2" />
              <p className="text-bundle-gold text-sm font-bold">{v.type === 'multi-scene' ? 'Building multi-scene video...' : 'Rendering with Sora 2...'}</p>
              <p className="text-white/60 text-xs mt-1">{v.progress || (v.type === 'multi-scene' ? 'Multiple scenes + voiceover + stitching' : '2–5 minutes typical')}</p>
            </div>
          ) : (
            <div className="text-center p-6">
              <p className="text-red-400 text-sm font-bold mb-1">Generation failed</p>
              <p className="text-white/40 text-xs line-clamp-2">{v.error}</p>
            </div>
          )}
        </div>

        <div className="p-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h3 className="font-heading font-bold text-bundle-green text-base truncate flex-1">{v.title}</h3>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${statusColor} uppercase`}>
              {v.status}
            </span>
          </div>
          <p className="text-xs text-gray-500 line-clamp-2 mb-2">{v.prompt}</p>
          <div className="flex items-center gap-2 text-[10px] text-gray-400 mb-3">
            <span>{v.duration}s</span>
            <span>·</span>
            <span>{v.size}</span>
            <span>·</span>
            <span>{v.model}</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            {v.status === 'ready' && !v.active && (
              <Button onClick={onActivate} size="sm" className="btn-bundle text-xs flex-1" data-testid={`activate-${v.video_id}`}>
                <Eye className="w-3 h-3 mr-1" /> Show on Dashboard
              </Button>
            )}
            {v.active && (
              <Button onClick={onDeactivate} size="sm" variant="outline" className="text-xs flex-1" data-testid={`deactivate-${v.video_id}`}>
                <EyeOff className="w-3 h-3 mr-1" /> Remove from Dashboard
              </Button>
            )}
            <Button onClick={onCopyPrompt} size="sm" variant="outline" className="text-xs">
              <Copy className="w-3 h-3" />
            </Button>
            <Button onClick={onDelete} size="sm" variant="outline" className="text-xs text-red-600 hover:bg-red-50">
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
